import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { kundenAPI } from '../services/api';
import { Plus, Search, Users, Building2 } from 'lucide-react';
import Navbar from '../components/Navbar';

const Kunden = () => {
  const [kunden, setKunden] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadKunden();
  }, []);

  const loadKunden = async () => {
    try {
      const data = await kundenAPI.getAll();
      setKunden(data);
    } catch (error) {
      console.error('Fehler beim Laden der Kunden:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredKunden = kunden.filter(kunde => 
    kunde.firma.toLowerCase().includes(searchTerm.toLowerCase()) ||
    kunde.kundennummer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (kunde.ort && kunde.ort.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <h1 className="text-3xl font-bold text-gray-900" data-testid="kunden-title">Kundenverwaltung</h1>
          <div className="flex gap-3">
            <Link
              to="/import"
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg transition"
              data-testid="import-button"
            >
              <Plus className="w-5 h-5" />
              Import aus VDKF-LEC
            </Link>
            <Link
              to="/kunde/neu"
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition"
              data-testid="new-kunde-button"
            >
              <Plus className="w-5 h-5" />
              Neuer Kunde
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Suchen nach Firma, Kundennummer oder Ort..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="search-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-lg text-gray-600">Laden...</div>
          </div>
        ) : filteredKunden.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Keine Kunden gefunden</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm 
                ? 'Versuchen Sie eine andere Suche'
                : 'Erstellen Sie Ihren ersten Kunden oder importieren Sie Daten aus VDKF-LEC'}
            </p>
            {!searchTerm && (
              <div className="flex gap-3 justify-center">
                <Link
                  to="/import"
                  className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg transition"
                >
                  <Plus className="w-5 h-5" />
                  Daten importieren
                </Link>
                <Link
                  to="/kunde/neu"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition"
                >
                  <Plus className="w-5 h-5" />
                  Ersten Kunden erstellen
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="kunden-grid">
            {filteredKunden.map((kunde) => (
              <div
                key={kunde.id}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition p-6 cursor-pointer"
                onClick={() => navigate(`/kunde/${kunde.id}`)}
                data-testid={`kunde-card-${kunde.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <Building2 className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{kunde.firma}</h3>
                      <p className="text-sm text-gray-500">KD-Nr: {kunde.kundennummer}</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  {kunde.ansprechpartner && (
                    <div>
                      <span className="text-gray-500">Ansprechpartner:</span>
                      <span className="ml-2 text-gray-900">{kunde.ansprechpartner}</span>
                    </div>
                  )}
                  {(kunde.plz || kunde.ort) && (
                    <div>
                      <span className="text-gray-500">Ort:</span>
                      <span className="ml-2 text-gray-900">{kunde.plz} {kunde.ort}</span>
                    </div>
                  )}
                  {kunde.telefon && (
                    <div>
                      <span className="text-gray-500">Telefon:</span>
                      <span className="ml-2 text-gray-900">{kunde.telefon}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Kunden;